import React from "react";
import {
  LayoutDashboard,
  BarChart3,
  HandHeart,
  Users,
  MessageCircle,
  Mail,
  UsersRound,
  KeyRound,
  FileText,
  MessagesSquare,
  CalendarDays,
  CalendarClock,
  Building2,
  CreditCard,
  TrendingUp,
  UserCog,
  ShieldCheck,
  Download,
  Loader2,
  Receipt,
  MailPlus,
  ClipboardList,
  CalendarRange,
} from "lucide-react";

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const TOKEN_KEY = "soarlife-admin-token";
export const ROLE_KEY = "soarlife-admin-role";
export const ROLE_LABEL_KEY = "soarlife-admin-role-label";
export const AREAS_KEY = "soarlife-admin-areas";
export const EMAIL_KEY = "soarlife-admin-email";

export function myEmail() {
  return localStorage.getItem(EMAIL_KEY) || "owner";
}

export function fmtDate(d) {
  if (!d) return "—";
  try {
    const dt = typeof d === "string" ? new Date(d) : d;
    return dt.toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return String(d);
  }
}

export function fmtBytes(bytes) {
  if (bytes === null || bytes === undefined) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export const APPT_TYPES = {
  consult: { label: "Consult", cls: "bg-[#899480]/20 text-[#3F5043]" },
  phone: { label: "Phone", cls: "bg-[#875F45]/15 text-[#875F45]" },
  paid: { label: "Paid", cls: "bg-[#3F5043] text-[#F8F7F3]" },
  "follow-up": { label: "Follow-up", cls: "bg-[#AE8F78]/30 text-[#3F5043]" },
};

export function dayKey(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export const BOOKING_DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
export const BOOKING_TIME_OPTS = [];
for (let h = 6; h <= 21; h++) {
  BOOKING_TIME_OPTS.push(`${String(h).padStart(2, "0")}:00`, `${String(h).padStart(2, "0")}:30`);
}

export const REPORTS = [
  { key: "revenue", label: "Revenue", desc: "All Stripe payments by type — memberships, bookings, clinics, supplements" },
  { key: "bookings", label: "Bookings", desc: "Online bookings by service and status" },
  { key: "client-progress", label: "Client Progress", desc: "Journey scores, pathways, and check-ins per client" },
  { key: "clinic-summary", label: "Clinic Summary", desc: "Interpreter requests and subscription status per clinic" },
  { key: "interpreter-utilization", label: "Interpreter Utilization", desc: "Assignments and completions per interpreter" },
  { key: "education-engagement", label: "Education Engagement", desc: "Content views and unique clients per item" },
  { key: "wellness", label: "Wellness & At-Risk", desc: "Mood check-ins, averages, and at-risk flags" },
];

export const TABS = [
  { key: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  { key: "clients", label: "Clients", icon: Users, area: "records" },
  { key: "overview", label: "Overview", icon: BarChart3 },
  { key: "attend", label: "Attend With Me", icon: HandHeart },
  { key: "consultations", label: "Consultations", icon: Users },
  { key: "chat_leads", label: "Chat Leads", icon: MessageCircle },
  { key: "newsletter", label: "Newsletter", icon: Mail },
  { key: "groups", label: "Community Groups", icon: UsersRound },
  { key: "codes", label: "Access Codes", icon: KeyRound },
  { key: "records", label: "Client Records", icon: FileText },
  { key: "chats", label: "Direct Chats", icon: MessagesSquare },
  { key: "appointments", label: "Appointments", icon: CalendarDays },
  { key: "schedule", label: "Team Schedule", icon: CalendarRange, area: "appointments" },
  { key: "booking", label: "Booking Calendar", icon: CalendarClock },
  { key: "clinics", label: "Clinics", icon: Building2 },
  { key: "invoices", label: "Invoices", icon: Receipt },
  { key: "emails", label: "Email Center", icon: MailPlus },
  { key: "notes", label: "Consult Notes", icon: ClipboardList },
  { key: "payments", label: "Payments", icon: CreditCard },
  { key: "reports", label: "Reports", icon: TrendingUp },
  { key: "staff", label: "Team", icon: UserCog },
  { key: "audit", label: "Audit Log", icon: ShieldCheck },
];


export function myAreas() {
  // All team members have full portal access (owner decision) — show every tab.
  return [...new Set(TABS.flatMap((t) => [t.key, t.area || t.key]))];
}

export const ROLE_OPTIONS = [
  ["physician", "Physician"],
  ["nurse", "Nurse"],
  ["care_coordinator", "Care Coordinator"],
  ["embryologist", "Embryologist"],
  ["clinic_admin", "Clinic Administrator"],
  ["company_admin", "Company Administrator"],
  ["super_admin", "Super Administrator"],
];

export const SCORE_STYLE = (s) =>
  s >= 90
    ? "bg-[#3F5043]/10 text-[#3F5043] border-[#3F5043]/30"
    : s >= 70
    ? "bg-[#D4AF37]/15 text-[#875F45] border-[#D4AF37]/50"
    : "bg-[#875F45]/15 text-[#875F45] border-[#875F45]/40";

export const NOTE_CATEGORIES = ["Consultation Notes", "Coaching Notes", "Medical Updates", "Follow-Up Notes", "Supplement Recommendations", "Wellness Concerns", "Financial Concerns"];

export const REQ_STATUS_CLS = {
  pending: "bg-[#AE8F78]/15 text-[#875F45] border-[#AE8F78]/40",
  confirmed: "bg-[#3F5043]/10 text-[#3F5043] border-[#3F5043]/30",
  completed: "bg-[#899480]/15 text-[#899480] border-[#899480]/40",
  canceled: "bg-[#875F45]/10 text-[#875F45]/70 border-[#875F45]/20",
};

export function TabWithExport({ source, title, children }) {
  const download = async () => {
    const t = localStorage.getItem(TOKEN_KEY);
    if (!t) return;
    try {
      const res = await fetch(`${API}/admin/export/${source}`, {
        headers: { Authorization: `Bearer ${t}` },
      });
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const cd = res.headers.get("Content-Disposition") || "";
      const match = cd.match(/filename="?([^"]+)"?/);
      a.download = match ? match[1] : `soarlife_${source}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert("Could not download CSV.");
    }
  };
  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="text-xs uppercase tracking-[0.22em] text-[#899480] font-semibold">
          {title}
        </div>
        <button
          onClick={download}
          data-testid={`admin-export-${source}`}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#3F5043] text-[#F8F7F3] text-xs uppercase tracking-[0.2em] font-semibold hover:bg-[#875F45] transition-colors"
        >
          <Download size={13} /> Export CSV
        </button>
      </div>
      {children}
    </div>
  );
}

export function StatCard({ label, value, sub }) {
  return (
    <div className="bg-[#F8F7F3] border border-[#BCB3A6]/40 rounded-2xl p-6">
      <div className="text-xs uppercase tracking-[0.22em] text-[#899480] font-semibold mb-2">
        {label}
      </div>
      <div className="font-serif text-5xl text-[#3F5043] leading-none">
        {value}
      </div>
      {sub && <div className="text-xs text-[#899480] mt-3">{sub}</div>}
    </div>
  );
}

export function Table({ headers, rows, testid, empty = "No records yet." }) {
  if (!rows.length)
    return (
      <div className="bg-[#F8F7F3] border border-[#BCB3A6]/40 rounded-2xl p-10 text-center text-[#899480]">
        {empty}
      </div>
    );
  return (
    <div className="bg-[#F8F7F3] border border-[#BCB3A6]/40 rounded-2xl overflow-hidden" data-testid={testid}>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#EDEDEE]">
            <tr>
              {headers.map((h) => (
                <th
                  key={h}
                  className="text-left px-4 py-3 text-[11px] uppercase tracking-[0.2em] text-[#899480] font-semibold"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr
                key={i}
                className="border-t border-[#BCB3A6]/30 hover:bg-[#EDEDEE]/50 align-top"
              >
                {r.map((cell, j) => (
                  <td key={j} className="px-4 py-3 text-[#3F5043]">
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export const GROUP_TEMPLATES = [
  {
    name: "IVF Cycle 1 · Peer Support",
    description: "A gentle private group for anyone starting their first IVF cycle. Share nerves, tips, and small wins.",
  },
  {
    name: "Haitian Creole Circle · Sèvis an Kreyòl",
    description: "Yon espas prive pou fanmi kap pale Kreyòl ayisyen. Pataje istwa w, poze kesyon, resevwa sipò.",
  },
  {
    name: "After a Loss",
    description: "A quiet, held space for anyone grieving pregnancy loss or a failed cycle. You are not alone.",
  },
  {
    name: "IUI Journey",
    description: "For patients navigating intrauterine insemination — timing, meds, questions, and encouragement.",
  },
  {
    name: "Egg & Embryo Freezing",
    description: "Fertility preservation support — before, during, and after your cycle.",
  },
  {
    name: "Pregnancy After Fertility Treatment",
    description: "The tender early days of pregnancy after IVF/IUI — gentle check-ins and shared relief.",
  },
  {
    name: "Donor Conception Support",
    description: "A thoughtful space for individuals and couples using donor eggs, sperm, or embryos.",
  },
];
